import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { motion, Variants } from 'framer-motion'
import { useLocation } from 'react-router'
import { useNavigate } from '../../../hooks/useNavigate'
import { Layout, Button, Spin, Tag } from 'antd'
import { PoweroffOutlined, SettingOutlined } from '@ant-design/icons'
import { authService } from '../../../services/AuthService'
import { PageTitle } from './page/Title'
import { store } from '../../../store'
import { BusinessSelector } from './page/BusinessSelector'
import { PageBreadcrumbs } from './page/Breadcrumbs'
import { routes } from '../../../config/routes'
import styles from './Page.module.scss'

const { Header, Content, Sider } = Layout

export interface Props {
  title?: string
  loading?: boolean
  actionText?: string
  onAction?: () => void
  titleActions?: React.ReactNode
  extraActions?: React.ReactNode
  sidebar?: React.ReactNode
  children?: React.ReactNode
}

export const Page = observer(({ title, loading, actionText, onAction, titleActions, extraActions, sidebar, children }: Props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const userProfile = store.profile.profile?.profile

  return (
    <div className={styles.root}>
      <Header className={styles.header}>
        <div className={styles.left}>
          <div className={styles.logo} onClick={() => navigate('/')}>Backoffice 2.0</div>
        </div>
        <div className={styles.right}>
          <BusinessSelector />
          <div className={styles.username}>{username(userProfile?.name, userProfile?.surname)}</div>
          <Tag color={'#ff6a00'}>super user</Tag>
          <Button
            type={'text'}
            className={styles.button}
            icon={<SettingOutlined />}
            onClick={() => navigate(routes.catalogList, { type: 'dynamic' })}
          />
          <Button
            type={'text'}
            className={styles.button}
            icon={<PoweroffOutlined />}
            onClick={() => authService.logout(true)}
          />
        </div>
      </Header>

      <div className={styles.subHeader}>
        <PageBreadcrumbs />
      </div>

      <div className={styles.inner}>
        {sidebar && (
          <Sider className={styles.sider} width={250}>
            {sidebar}
          </Sider>
        )}

        <Layout style={{ position: 'relative' }}>
          {loading
            ? (
            <div className={styles.loader}>
              <Spin size="large" />
            </div>
          ) : (
            <Content>
              {title && (
                <PageTitle
                  title={title}
                  actionText={actionText}
                  onAction={onAction}
                  titleActions={titleActions}
                  extraActions={extraActions}
                />
              )}
              <motion.div
                className={cn(styles.content, sidebar && styles.collapse)}
                variants={varsContent}
                initial={'init'}
                animate={'enter'}
              >
                {children}
              </motion.div>
            </Content>
          )}
        </Layout>
      </div>
    </div>
  )
})

function username(name?: string, surname?: string) {
  return name === surname ? name : `${name || ''} ${surname || ''}`.trim()
}

const varsContent: Variants = {
  init:  { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.2 } },
}
